import { useState, useEffect, useRef } from "react";
import { Button, Form, Card } from "react-bootstrap";
import jsPDF from "jspdf";

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition;

const MinutesRecorderScreen = () => {
  const [text, setText] = useState(localStorage.getItem("minutes") || "");
  const [listening, setListening] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [language, setLanguage] = useState("en-US");

  const recognitionRef = useRef(null);
  const timerRef = useRef(null);

  const formatText = (raw) => {
    let t = raw.trim();

    if (!t) return "";

    if (!/[.!?]$/.test(t)) {
      t += ".";
    }

    t = t.charAt(0).toUpperCase() + t.slice(1);

    return t;
  };

  const startTimer = () => {
    setSeconds(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const startRecording = () => {
    if (!SpeechRecognition) {
      alert("Voice recognition not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = language;
    recognition.interimResults = true;
    recognition.continuous = true;

    recognitionRef.current = recognition;
    setListening(true);
    startTimer();
    recognition.start();

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript + " ";
      }

      const formatted = formatText(transcript);

      setText((prev) => {
        if (!prev) return formatted;
        return prev + "\n" + formatted;
      });
    };

    recognition.onerror = () => {
      setListening(false);
      stopTimer();
    };

    recognition.onend = () => {
      setListening(false);
      stopTimer();
    };
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setListening(false);
    stopTimer();
  };

  const resetPage = () => {
    stopRecording();
    setText("");
    setSeconds(0);
    localStorage.removeItem("minutes");
  };

  useEffect(() => {
    localStorage.setItem("minutes", text);
  }, [text]);

  const exportPDF = () => {
    const doc = new jsPDF();
    const lines = doc.splitTextToSize(text, 180);
    doc.text(lines, 10, 10);
    doc.save("minutes.pdf");
  };

  const exportWord = () => {
    const blob = new Blob([text], {
      type: "application/msword",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "minutes.doc";
    link.click();
  };

  const copyText = () => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const formatDuration = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <div className={darkMode ? "bg-dark text-light p-4" : "p-4"}>
      <h2 className="mb-3">Meeting Minutes Recorder</h2>

      <Form.Check
        type="switch"
        id="dark-mode-switch"
        label="Dark Mode"
        checked={darkMode}
        onChange={() => setDarkMode(!darkMode)}
        className="mb-3"
      />

      <Card className={`p-3 shadow-sm ${darkMode ? "bg-secondary text-light" : ""}`}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <strong>Recording Time:</strong> {formatDuration(seconds)}
          </div>

          <Form.Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{
              maxWidth: "220px",
              backgroundColor: darkMode ? "#333" : "white",
              color: darkMode ? "white" : "black",
            }}
          >
            <option value="en-US">English (US)</option>
            <option value="en-GB">English (UK)</option>
            <option value="fr-FR">French</option>
            <option value="es-ES">Spanish</option>
            <option value="ar-SA">Arabic</option>
          </Form.Select>
        </div>

        <div className="d-flex gap-2 mb-3 flex-wrap">
          <Button
            variant={listening ? "danger" : "primary"}
            onClick={startRecording}
            disabled={listening}
            style={{
              position: "relative",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                display: "inline-block",
                marginRight: "6px",
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                backgroundColor: listening ? "#ff4d4d" : "#ffffff",
                boxShadow: listening
                  ? "0 0 8px rgba(255, 77, 77, 0.9)"
                  : "none",
                animation: listening ? "pulse 1s infinite" : "none",
              }}
            ></span>
            {listening ? "Listening..." : "Start Recording"}
          </Button>

          <Button
            variant="warning"
            onClick={stopRecording}
            disabled={!listening}
          >
            Stop Recording
          </Button>

          <Button
            variant="secondary"
            onClick={resetPage}
          >
            Reset Page
          </Button>
        </div>

        <Form.Control
          as="textarea"
          rows={12}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Your meeting minutes will appear here..."
          style={{
            backgroundColor: darkMode ? "#333" : "white",
            color: darkMode ? "white" : "black",
          }}
        />

        <div className="d-flex gap-2 mt-3 flex-wrap">
          <Button variant="success" onClick={copyText}>
            Copy Text
          </Button>

          <Button variant="info" onClick={exportPDF}>
            Export PDF
          </Button>

          <Button variant="secondary" onClick={exportWord}>
            Export Word
          </Button>
        </div>
      </Card>

      <Button
        variant={listening ? "danger" : "primary"}
        onClick={listening ? stopRecording : startRecording}
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          borderRadius: "50%",
          width: "60px",
          height: "60px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
          fontSize: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: listening ? "pulse 1s infinite" : "none",
        }}
      >
        🎤
      </Button>

      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              box-shadow: 0 0 0 rgba(255, 77, 77, 0.7);
            }
            50% {
              transform: scale(1.1);
              box-shadow: 0 0 12px rgba(255, 77, 77, 1);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 0 0 rgba(255, 77, 77, 0.7);
            }
          }
        `}
      </style>
    </div>
  );
};

export default MinutesRecorderScreen;
